<template>
  <div class="sheet-columns-config-page">
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10" xl="8">
          <!-- Page Header -->
          <div class="page-header mb-6">
            <h1 class="page-title">Sheet Configuration</h1>
            <p class="page-subtitle">
              Configure Google Sheets integration and monitor processing status
            </p>
          </div>

          <!-- Main Component -->
          <SheetColumnsConfig
            @configuration-saved="onConfigurationSaved"
            @configuration-error="onConfigurationError"
            @stats-refreshed="onStatsRefreshed"
            @stats-error="onStatsError"
            @history-loaded="onHistoryLoaded"
            @history-error="onHistoryError"
            @view-history-details="onViewHistoryDetails"
            @download-history-results="onDownloadHistoryResults"
            @history-item-deleted="onHistoryItemDeleted"
            @history-cleared="onHistoryCleared"
            @clipboard-copied="onClipboardCopied"
            @clipboard-error="onClipboardError"
            @download-error="onDownloadError"
            @delete-error="onDeleteError"
            @clear-error="onClearError"
            @excel-processed="onExcelProcessed"
            @excel-error="onExcelError"
            @columns-applied="onColumnsApplied"
          />

          <!-- Additional Info Card -->
          <v-card class="mt-6" elevation="1">
            <v-card-title>
              <v-icon left>mdi-information</v-icon>
              Configuration Guide
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-format-title</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Title</v-list-item-title>
                    <v-list-item-subtitle>
                      Enter a descriptive name for this sheet configuration
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-source-branch</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Source</v-list-item-title>
                    <v-list-item-subtitle>
                      Select the work source from the available options
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-google-sheets</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Sheet ID</v-list-item-title>
                    <v-list-item-subtitle>
                      Found in your Google Sheets URL: https://docs.google.com/spreadsheets/d/[SHEET_ID]/edit
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-table-column</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Column Numbers</v-list-item-title>
                    <v-list-item-subtitle>
                      Column A = 1, Column B = 2, Column C = 3, etc.
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-chart-line</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Processing Status</v-list-item-title>
                    <v-list-item-subtitle>
                      Monitor duplicate detection and new user processing in real-time
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Global Snackbar for notifications -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      top
      right
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import SheetColumnsConfig from '@/components/AdminComponent/SheetColumnsConfig.vue'

export default {
  name: 'SheetColumnsConfigPage',
  components: {
    SheetColumnsConfig
  },
  data() {
    return {
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      snackbarTimeout: 4000
    }
  },
  methods: {
    onConfigurationSaved(configData) {
      this.showNotification('Configuration saved successfully!', 'success')
      console.log('Configuration saved:', configData)
    },
    
    onConfigurationError(error) {
      this.showNotification('Error saving configuration', 'error')
      console.error('Configuration error:', error)
    },
    
    onStatsRefreshed(stats) {
      this.showNotification('Stats refreshed successfully', 'info')
      console.log('Stats refreshed:', stats)
    },
    
    onStatsError(error) {
      this.showNotification('Error refreshing stats', 'error')
      console.error('Stats error:', error)
    },
    
    onHistoryLoaded(historyData) {
      this.showNotification(`Loaded ${historyData.length} history items`, 'info')
      console.log('History loaded:', historyData)
    },
    
    onHistoryError(error) {
      this.showNotification('Error loading history', 'error')
      console.error('History error:', error)
    },
    
    onViewHistoryDetails(item) {
      this.showNotification(`Viewing details for: ${item.title}`, 'info')
      console.log('View history details:', item)
      // You could open a dialog or navigate to details page here
    },
    
    onDownloadHistoryResults(item) {
      this.showNotification(`Downloading results for: ${item.title}`, 'info')
      console.log('Download history results:', item)
      // You could trigger file download here
    },
    
    onHistoryItemDeleted(item) {
      this.showNotification(`Deleted: ${item.title}`, 'success')
      console.log('History item deleted:', item)
    },
    
    onHistoryCleared() {
      this.showNotification('History cleared successfully', 'success')
      console.log('History cleared')
    },
    
    onClipboardCopied(text) {
      this.showNotification('Copied to clipboard', 'success')
      console.log('Clipboard copied:', text)
    },
    
    onClipboardError(error) {
      this.showNotification('Failed to copy to clipboard', 'error')
      console.error('Clipboard error:', error)
    },
    
    onDownloadError(error) {
      this.showNotification('Failed to download results', 'error')
      console.error('Download error:', error)
    },
    
    onDeleteError(error) {
      this.showNotification('Failed to delete history item', 'error')
      console.error('Delete error:', error)
    },
    
    onClearError(error) {
      this.showNotification('Failed to clear history', 'error')
      console.error('Clear error:', error)
    },
    
    onExcelProcessed(data) {
      this.showNotification('Excel file processed successfully', 'success')
      console.log('Excel processed:', data)
    },
    
    onExcelError(error) {
      this.showNotification('Error processing Excel file', 'error')
      console.error('Excel error:', error)
    },
    
    onColumnsApplied(columns) {
      this.showNotification('Column indices applied successfully', 'success')
      console.log('Columns applied:', columns)
    },
    
    showNotification(message, type = 'info') {
      this.snackbarMessage = message
      this.snackbarColor = type
      this.showSnackbar = true
    }
  }
}
</script>

<style scoped>
.sheet-columns-config-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 2rem 0;
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 300;
  color: #333;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin: 0;
}

/* Dark mode support */
.dark .sheet-columns-config-page {
  background-color: #121212;
}

.dark .page-title {
  color: #fff;
}

.dark .page-subtitle {
  color: #b0b0b0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .sheet-columns-config-page {
    padding: 1rem 0;
  }
}
</style>
